import { ChangeDetectorRef, Component, forwardRef, Input } from "@angular/core";
import { ActionSheetController } from "@ionic/angular";
import { AssignmentDefinition } from "source/app/features/assignments/assignment.interface";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AssignmentService } from "source/app/features/assignments/services/assignment.service";
import { TranslocoService } from "@ngneat/transloco";
import { AssignmentWithData } from "../../../checklist.interface";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { format, isToday, isTomorrow, parseISO } from "date-fns";

@Component({
	selector: "app-assignments",
	templateUrl: "./assignments.component.html",
	styleUrls: ["./assignments.component.scss"],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AssignmentsComponent),
			multi: true,
		},
	],
})
export class AssignmentsComponent implements ControlValueAccessor {
	constructor(
		private translocoService: TranslocoService,
		private actionSheetController: ActionSheetController,
		private assignmentService: AssignmentService,
		private changeDetector: ChangeDetectorRef,
		private rideOpsUi: RideOpsUiService,
	) {}

	@Input() questionId: string;

	private assignments: AssignmentWithData[] = [];
	public visibleAssignment: AssignmentWithData[] = [];

	isV2 = (): boolean => this.rideOpsUi.isV2Active();

	private filterVisibleAssignments = (assignments: AssignmentWithData[]): AssignmentWithData[] => {
		return assignments.filter((x) => x.triggeredBy == null || x.triggeredBy.requireNoIntervention == false);
	};

	private createButton = (action: string, text: string, role: string | undefined = undefined) => {
		return {
			text: this.translocoService.translate(text),
			role,
			data: {
				action,
			},
		};
	};

	private editAssignment = async (assignment: AssignmentWithData) => {
		const editedAssignment = await this.assignmentService.openAssignment(assignment);

		if (editedAssignment != null) {
			const index = this.assignments.findIndex((x) => x === assignment);
			this.assignments.splice(index, 1, editedAssignment);

			this.updateForm();
		}
	};

	option = async (assignment: AssignmentWithData) => {
		if (assignment.triggeredBy != null) {
			return this.editAssignment(assignment);
		}

		const cancelButton = this.createButton("cancel", "BASE__CANCEL", "cancel");
		const editButton = this.createButton("edit", "ASSIGNMENT__OPTIONS_EDIT");
		const removeButton = this.createButton("delete", "ASSIGNMENT__OPTIONS_REMOVE", "destructive");

		const actionSheet = await this.actionSheetController.create({
			header: this.translocoService.translate("ASSIGNMENT__OPTIONS"),
			mode: "ios",
			buttons: [editButton, removeButton, cancelButton],
		});

		await actionSheet.present();
		const action = await actionSheet.onWillDismiss();

		if (action.role === "destructive") {
			this.assignments = this.assignments.filter((x) => x != assignment);

			this.updateForm();
		}
		if (action.data?.action === "edit") {
			await this.editAssignment(assignment);
		}
	};

	statusLabel = (assignment: AssignmentWithData): string => {
		const fromType = this.statusKeyFromStateType(this.definitionStateType(assignment));
		if (fromType) {
			return this.translocoService.translate(fromType);
		}
		const fromStatus = this.statusKeyFromStatus(assignment.assignment.status);
		if (fromStatus) {
			return this.translocoService.translate(fromStatus);
		}
		if (assignment.solution) {
			return this.translocoService.translate("ASSIGNMENT__STATUS_FINISHED");
		}
		return this.translocoService.translate("ASSIGNMENT__STATUS_CREATED");
	};

	private definitionStateType = (assignment: AssignmentWithData): string | undefined => {
		if (assignment.solution?.definitionState?.type) {
			return assignment.solution.definitionState.type;
		}
		const state = assignment.assignment.definitionState as { type?: string } | undefined;
		return state?.type;
	};

	private statusKeyFromStateType = (type?: string): string | null => {
		switch (type) {
			case "Initial":
				return "ASSIGNMENT__STATUS_CREATED";
			case "Step":
				return "ASSIGNMENT__STATUS_STARTED";
			case "Final":
				return "ASSIGNMENT__STATUS_FINISHED";
			default:
				return null;
		}
	};

	private statusKeyFromStatus = (status?: string): string | null => {
		switch ((status || "").toLowerCase()) {
			case "created":
				return "ASSIGNMENT__STATUS_CREATED";
			case "started":
				return "ASSIGNMENT__STATUS_STARTED";
			case "finished":
				return "ASSIGNMENT__STATUS_FINISHED";
			default:
				return null;
		}
	};

	assigneeLabel = (assignment: AssignmentWithData): string => {
		const names = (assignment.assignment.assignees || []).map((a) => a.name).filter(Boolean);
		return names.join(", ");
	};

	deadlineLabel = (assignment: AssignmentWithData): string => {
		const end = assignment.assignment.end;
		if (!end) {
			return "";
		}
		const date = typeof end === "string" ? parseISO(end) : new Date(end);
		if (Number.isNaN(date.getTime())) {
			return "";
		}
		if (isToday(date)) {
			return this.translocoService.translate("ASSIGNMENT_CARD__DUE_TODAY");
		}
		if (isTomorrow(date)) {
			return this.translocoService.translate("ASSIGNMENT_CARD__DUE_TOMORROW");
		}
		return this.translocoService.translate("ASSIGNMENT_CARD__DUE_ON", { date: format(date, "MMM d") });
	};

	priorityLabel = (assignment: AssignmentWithData): string => {
		if (assignment.assignment.definitionPriority?.name) {
			return assignment.assignment.definitionPriority.name;
		}
		if (assignment.assignment.priority) {
			return this.translocoService.translate("ASSIGNMENT__PRIORITY_YES");
		}
		return "";
	};

	isHighPriority = (assignment: AssignmentWithData): boolean => !!assignment.assignment.priority;

	evidenceLabel = (assignment: AssignmentWithData): string => {
		const hasDescription = !!(assignment.assignment.description || "").replace(/<[^>]+>/g, "").trim();
		const imageCount = assignment.assignment.attachments?.length ?? 0;
		const parts: string[] = [];
		if (hasDescription) {
			parts.push(this.translocoService.translate("ASSIGNMENT__DESCRIPTION"));
		}
		if (imageCount > 0) {
			parts.push(
				this.translocoService.translate("ASSIGNMENT_CARD__IMAGES", { count: imageCount }),
			);
		}
		return parts.join(" · ");
	};

	private updateForm = () => {
		this.onTouched();
		this.onChange(this.assignments);
		this.changeDetector.markForCheck();
		this.visibleAssignment = this.filterVisibleAssignments(this.assignments);
	};

	createAssignment = async () => {
		let definition: AssignmentDefinition;
		try {
			definition = await this.assignmentService.selectDefinition();
		} catch {
			return;
		}

		const assignment = await this.assignmentService.createAssignment(this.questionId, definition);

		if (assignment != null) {
			const assignments = [...this.assignments, assignment];
			this.assignments = assignments;

			this.updateForm();
		}
	};

	onChange = (_assignments: AssignmentWithData[]) => {};
	onTouched = () => {};
	disabled = false;

	writeValue(assignments: AssignmentWithData[]): void {
		this.assignments = assignments;
		this.visibleAssignment = this.filterVisibleAssignments(assignments);
	}

	registerOnChange(fn: (value: AssignmentWithData[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}
